import { useCallback, useState } from 'react'
import { Navigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useReportsApi } from '@/hooks'
import { ReportEditUsageAndCost } from '@/features/Reporting/components/ReportEdit/ReportEditUsageAndCost'
import { ReportEditPayablesSummary } from '@/features/Reporting/components/ReportEdit/ReportEditPayablesSummary'
import { payablesSummarySearchSchema } from '@/schemas'
import { ExitReportCustomizationContent } from '@/components/molecules/ExitReportCustomizationContent/ExitReportCustomizationContent'
import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'
import { ReportEditBenchmarking } from '@/features/Reporting/components/ReportEdit/ReportEditBenchmarking'
import { ReportEditTotalEmissions } from '@/features/Reporting/components/ReportEdit/ReportEditTotalEmissions'
import { ReportEditGHGEmissions } from '@/features/Reporting/components/ReportEdit/ReportEditGHGEmissions'
import { ReportEditIntervalData } from '@/features/Reporting/components/ReportEdit/ReportEditIntervalData'

type ReportEditProps = {
    reportId: string
}

export const ReportEdit: React.FC<ReportEditProps> = (props) => {
    const { t } = useTranslation('translations')
    const { reportId } = props

    const { useGetReport } = useReportsApi()
    const { data, isSuccess } = useGetReport(reportId)
    const [isOpen, setIsOpen] = useState(false)

    const search = useSearch({
        from: '/_layout/reporting/$reportId/edit',
    })

    const targetSearch =
        data?.type === 'payables-summary'
            ? payablesSummarySearchSchema.parse(search)
            : {}

    const setModalState = useGlobalModalStore((state) => state.setModalState)
    const setOnNavigationBlockedHandler = useNavigationGuardStore(
        (state) => state.setOnNavigationBlockedHandler
    )

    const closeModal = useCallback(() => {
        setModalState({ isOpen: false })
    }, [setModalState])

    const openExitCustomizationModal = useCallback(() => {
        setModalState({
            isOpen: true,
            title: t('reporting.exit_customization.title'),
            icon: 'warning',
            description: t('reporting.exit_customization.description'),
            content: () => <ExitReportCustomizationContent />,
            onClose: closeModal,
        })
    }, [setModalState, t, closeModal])

    setOnNavigationBlockedHandler(openExitCustomizationModal)

    if (isSuccess && JSON.stringify(targetSearch) !== JSON.stringify(search)) {
        return (
            <Navigate
                to={`/reporting/$reportId/edit`}
                params={{ reportId }}
                search={targetSearch}
                replace={true}
            />
        )
    }

    if (!data) {
        return null
    }

    if (data.type === 'payables-summary') {
        return <ReportEditPayablesSummary data={data} />
    }

    if (data.type === 'benchmarking') {
        return (
            <ReportEditBenchmarking
                data={data}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
            />
        )
    }

    if (data.type === 'total-emissions') {
        return (
            <ReportEditTotalEmissions
                data={data}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
            />
        )
    }

    if (data.type === 'ghg-emissions') {
        return (
            <ReportEditGHGEmissions
                data={data}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
            />
        )
    }

    if (data.type === 'interval-data') {
        return (
            <ReportEditIntervalData
                data={data}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
            />
        )
    }

    return (
        <ReportEditUsageAndCost
            data={data}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
        />
    )
}
